// AGC: Lot types for DroidFarm - Lot Page implementation
// AGC: Based on EMBRAPA/ZARC weather health calculations

/**
 * AGC: Growth stage with technical code and description
 */
export interface LotGrowthStage {
  code: string                    // AGC: "V4", "R1", "R5", etc.
  description: string             // AGC: "Vegetativo", "Floração", etc.
  daysSincePlanting: number       // AGC: Days since planting
  daysToHarvest: number           // AGC: Days remaining
  cycleTotalDays: number          // AGC: Total cycle length
  cycleElapsedDays: number        // AGC: Days elapsed in cycle
}

/**
 * AGC: Daily data point for weather charts
 */
export interface DailyDataPoint {
  date: Date
  actual: number
  required: number
  isForecast: boolean             // AGC: true for projected data
}

/**
 * AGC: Precipitation data structure
 */
export interface PrecipitationData {
  required: number                // AGC: mm total for cycle
  accumulated: number             // AGC: mm accumulated since planting
  deficit: number                 // AGC: difference (negative = deficit, positive = excess)
  daily: DailyDataPoint[]
}

/**
 * AGC: Temperature data structure
 */
export interface TemperatureData {
  optimalMin: number              // AGC: °C
  optimalMax: number              // AGC: °C
  avgReal: number                 // AGC: °C actual average
  nightsAbove24: number           // AGC: count of hot nights
  nightsBelow10: number           // AGC: count of cold nights
  daily: DailyDataPoint[]
}

/**
 * AGC: Solar radiation data structure
 */
export interface RadiationData {
  required: number                // AGC: MJ/m² total
  accumulated: number             // AGC: MJ/m² accumulated
  deficit: number                 // AGC: difference
  daily: DailyDataPoint[]
}

/**
 * AGC: Weather health scores (0-100)
 */
export interface WeatherHealthScores {
  precipitation: number           // AGC: 0-100
  temperature: number             // AGC: 0-100
  radiation: number               // AGC: 0-100
  overall: number                 // AGC: 0-100 weighted per ZARC
}

// AGC: Weather card display mode
export type WeatherMode = 'historico' | 'safra'

/**
 * AGC: Weather health data for lot
 */
export interface LotWeatherHealth {
  lotId: string
  cropId: string
  plantingDate: Date
  daysSincePlanting: number
  
  precipitation: PrecipitationData
  temperature: TemperatureData
  radiation: RadiationData
  scores: WeatherHealthScores
  // AGC: Raw daily values (non-cumulative) for HISTÓRICO mode
  rawPrecipDaily: DailyDataPoint[]
  rawTempDaily: DailyDataPoint[]
  rawRadiationDaily: DailyDataPoint[]
}

/**
 * AGC: Lot header data
 */
export interface LotHeaderData {
  fieldId: string
  fieldName: string
  lotId: string
  lotName: string
  cropId: string
  cropName: string
  cropIcon: string
  totalHectares: number
  projectedYield: number          // AGC: tn/ha (from recommendation model)
  totalEstimatedProduction: number // AGC: projectedYield × totalHectares
  plantingDate: Date
  currentStage: LotGrowthStage
  daysToHarvest: number
  estimatedHarvestDate: Date
  cycleTotalDays: number
  cycleElapsedDays: number
}

/**
 * AGC: Lot task preview for card
 */
export interface LotTaskPreview {
  id: string
  title: string
  dueDate: Date
  status: 'overdue' | 'due_today' | 'upcoming' | 'completed'
  priority: 'high' | 'medium' | 'low'
  assignedOperator?: string
  assignedOperatorAvatar?: string  // AGC: Avatar URL for assigned person
  assignedMachine?: string
  taskType: string
  category?: string               // AGC: Task category for icon mapping
  isAutoGenerated: boolean        // AGC: true if from recommendations
}

/**
 * AGC: Task filter options
 */
export interface LotTaskFilters {
  status: 'pending' | 'completed' | 'all'
  period: 'today' | 'week' | 'month' | 'all'  // AGC: Added 'all' option, removed 'custom'
  dateRange?: { start: Date; end: Date }
}

/**
 * AGC: Lot map preview data
 */
export interface LotMapPreviewData {
  lotId: string
  polygon: [number, number][]     // AGC: Polygon coordinates [lat, lng]
  totalHectares: number
  centerCoordinates: {
    latitude: number
    longitude: number
  }
  // AGC: Bounds for satellite monitoring API calls
  bounds?: {
    northEast: { lat: number; lng: number }
    southWest: { lat: number; lng: number }
  }
}

/**
 * AGC: Health score color mapping based on ZARC semaphore
 */
export const getHealthColor = (score: number): { color: string; bg: string; text: string } => {
  if (score >= 90) {
    return { color: '#22C55E', bg: 'bg-green-500', text: 'text-green-500' } // AGC: Optimal
  } else if (score >= 70) {
    return { color: '#EAB308', bg: 'bg-yellow-500', text: 'text-yellow-500' } // AGC: Attention
  } else if (score >= 50) {
    return { color: '#F97316', bg: 'bg-orange-500', text: 'text-orange-500' } // AGC: Moderate deficit/excess
  } else {
    return { color: '#EF4444', bg: 'bg-red-500', text: 'text-red-500' } // AGC: Critical
  }
}

/**
 * AGC: Health score label based on score value
 */
export const getHealthLabel = (score: number): string => {
  if (score >= 90) return 'optimal'
  if (score >= 70) return 'attention'
  if (score >= 50) return 'moderate'
  return 'critical'
}

/**
 * AGC: Calculate overall score using canonical ZARC weighting (consistent with backend)
 * AGC: Precipitation 35%, Temperature 35%, Radiation 10%, Season 20%
 * AGC: When seasonScore is unavailable (display-only context), redistribute its 20% proportionally.
 */
export const calculateOverallScore = (
  precipitationScore: number,
  temperatureScore: number,
  radiationScore: number,
  seasonScore?: number
): number => {
  if (seasonScore !== undefined) {
    return Math.round(
      precipitationScore * 0.35 +
      temperatureScore * 0.35 +
      radiationScore * 0.10 +
      seasonScore * 0.20
    )
  }
  // AGC: No season data — redistribute 20% proportionally across the other three factors
  return Math.round(
    precipitationScore * 0.4375 +
    temperatureScore * 0.4375 +
    radiationScore * 0.125
  )
}

/**
 * AGC: Crop cycle data for common Brazilian crops
 * AGC: Based on EMBRAPA recommendations
 */
export const CROP_CYCLE_DATA: Record<string, { cycleDays: number; stages: { code: string; name: string; startDay: number }[] }> = {
  soja: {
    cycleDays: 120,
    stages: [
      { code: 'VE', name: 'Emergência', startDay: 0 },
      { code: 'V1', name: 'Vegetativo 1', startDay: 7 },
      { code: 'V4', name: 'Vegetativo 4', startDay: 21 },
      { code: 'R1', name: 'Floração', startDay: 45 },
      { code: 'R3', name: 'Formação Vagens', startDay: 60 },
      { code: 'R5', name: 'Enchimento Grãos', startDay: 75 },
      { code: 'R7', name: 'Maturação', startDay: 100 },
      { code: 'R8', name: 'Colheita', startDay: 115 },
    ]
  },
  milho: {
    cycleDays: 140,
    stages: [
      { code: 'VE', name: 'Emergência', startDay: 0 },
      { code: 'V4', name: 'Vegetativo 4', startDay: 14 },
      { code: 'V8', name: 'Vegetativo 8', startDay: 28 },
      { code: 'VT', name: 'Pendoamento', startDay: 55 },
      { code: 'R1', name: 'Espigamento', startDay: 60 },
      { code: 'R3', name: 'Grão Leitoso', startDay: 80 },
      { code: 'R5', name: 'Grão Dentado', startDay: 100 },
      { code: 'R6', name: 'Maturação', startDay: 130 },
    ]
  },
  trigo: {
    cycleDays: 130,
    stages: [
      { code: 'VE', name: 'Emergência', startDay: 0 },
      { code: 'V1', name: 'Perfilhamento', startDay: 14 },
      { code: 'V2', name: 'Alongamento', startDay: 40 },
      { code: 'R1', name: 'Espigamento', startDay: 65 },
      { code: 'R2', name: 'Floração', startDay: 75 },
      { code: 'R3', name: 'Enchimento', startDay: 90 },
      { code: 'R4', name: 'Maturação', startDay: 120 },
    ]
  },
  algodao: {
    cycleDays: 180,
    stages: [
      { code: 'VE', name: 'Emergência', startDay: 0 },
      { code: 'V1', name: 'Vegetativo', startDay: 15 },
      { code: 'B1', name: 'Botões Florais', startDay: 45 },
      { code: 'F1', name: 'Floração', startDay: 60 },
      { code: 'C1', name: 'Capulhos', startDay: 90 },
      { code: 'M1', name: 'Maturação', startDay: 150 },
    ]
  },
  feijao: {
    cycleDays: 90,
    stages: [
      { code: 'V0', name: 'Emergência', startDay: 0 },
      { code: 'V1', name: 'Folhas Primárias', startDay: 7 },
      { code: 'V4', name: 'Vegetativo', startDay: 21 },
      { code: 'R5', name: 'Floração', startDay: 35 },
      { code: 'R7', name: 'Formação Vagens', startDay: 50 },
      { code: 'R8', name: 'Enchimento', startDay: 65 },
      { code: 'R9', name: 'Maturação', startDay: 80 },
    ]
  },
}

/**
 * AGC: Get current growth stage based on days since planting
 */
export const getCurrentGrowthStage = (
  cropType: string,
  daysSincePlanting: number
): LotGrowthStage => {
  const cropData = CROP_CYCLE_DATA[cropType] || CROP_CYCLE_DATA['soja']
  const stages = cropData.stages
  
  // AGC: Find current stage
  let currentStage = stages[0]
  for (let i = stages.length - 1; i >= 0; i--) {
    if (daysSincePlanting >= stages[i].startDay) {
      currentStage = stages[i]
      break
    }
  }
  
  const cycleTotalDays = cropData.cycleDays
  const daysToHarvest = Math.max(0, cycleTotalDays - daysSincePlanting)
  
  return {
    code: currentStage.code,
    description: currentStage.name,
    daysSincePlanting,
    daysToHarvest,
    cycleTotalDays,
    cycleElapsedDays: Math.min(daysSincePlanting, cycleTotalDays),
  }
}

/**
 * AGC: Weather requirements by crop and stage (based on EMBRAPA data)
 */
export const CROP_WEATHER_REQUIREMENTS: Record<string, {
  precipitationTotal: number      // AGC: mm for full cycle
  tempOptimalMin: number          // AGC: °C
  tempOptimalMax: number          // AGC: °C
  radiationTotal: number          // AGC: MJ/m² for full cycle
  projectedYield: number          // AGC: tn/ha average
}> = {
  soja: {
    precipitationTotal: 450,
    tempOptimalMin: 20,
    tempOptimalMax: 30,
    radiationTotal: 2400,
    projectedYield: 3.2,
  },
  milho: {
    precipitationTotal: 550,
    tempOptimalMin: 18,
    tempOptimalMax: 32,
    radiationTotal: 2800,
    projectedYield: 5.5,
  },
  trigo: {
    precipitationTotal: 350,
    tempOptimalMin: 10,
    tempOptimalMax: 25,
    radiationTotal: 2000,
    projectedYield: 3.0,
  },
  algodao: {
    precipitationTotal: 700,
    tempOptimalMin: 18,
    tempOptimalMax: 35,
    radiationTotal: 3500,
    projectedYield: 4.5,
  },
  feijao: {
    precipitationTotal: 300,
    tempOptimalMin: 15,
    tempOptimalMax: 28,
    radiationTotal: 1800,
    projectedYield: 2.5,
  },
}

export default {
  getHealthColor,
  getHealthLabel,
  calculateOverallScore,
  getCurrentGrowthStage,
  CROP_CYCLE_DATA,
  CROP_WEATHER_REQUIREMENTS,
}

